/**
 * Style feature extraction for P5 (docs/proposals/ai-personas-voice-and-sound.md
 * §Playing styles): from one candidate move, the position facts a style score
 * ranks by — "captures, checks and forward pawn moves raise aggression;
 * defended pieces, king safety and trades when ahead raise caution".
 *
 * Everything is derived from `(before, after)` only — no second evaluation
 * pass, no history walks. A candidate whose move cannot be applied (should
 * never happen: candidates come from the engine's legal top-N) makes the
 * extractor return null and the candidate is dropped by the re-ranker.
 *
 * Lives in `game-engine`, not `chess-ai`: chess-ai is UCI plumbing with no
 * board model (spec §Architecture puts this "beside each engine"; see the PR
 * body for the deviation note).
 */
import type { PositionSnapshot, Turn } from '@strategy4kids/protocol';
import { pieceValue } from '../attack-table.js';
import { squareToIndex } from '../coords.js';
import type { GameAdapter } from '../game-adapter.js';

/** All axes 0..1. */
export interface StyleFeatures {
  /** Material taken by this move, normalised: chess pawn=1/9…queen=1; checkers man≈0.33, king=1. */
  readonly capture01: number;
  /** The move gives check (always 0 for checkers — it has no check). */
  readonly givesCheck: number;
  /** A man/pawn moved one step toward promotion. */
  readonly advance01: number;
  /** The landing square is attacked by the mover's own colour in `after`. */
  readonly defended: number;
  /** Chess: the king's neighbourhood came under no MORE attacker pressure. Checkers: the landing square is not attacked. */
  readonly safe: number;
  /** A capture taken while already ahead by at least a pawn (chess) / a man (checkers). */
  readonly tradeAhead: number;
}

const KING_NEIGHBOURHOOD = [
  [-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 0], [0, 1], [1, -1], [1, 0], [1, 1],
] as const;

/** Squares (as indices) a square's 8-neighbourhood covers, itself included. */
function neighbourhood(index: number): number[] {
  const out: number[] = [];
  for (const [dr, dc] of KING_NEIGHBOURHOOD) {
    const r = Math.floor(index / 8) + dr;
    const c = (index % 8) + dc;
    if (r < 0 || r > 7 || c < 0 || c > 7) continue;
    out.push(r * 8 + c);
  }
  return out;
}

function attackerCount(mask: Uint8Array, indices: readonly number[]): number {
  let n = 0;
  for (const i of indices) n += mask[i] ?? 0;
  return n;
}

function advancesTowardPromotion(mover: Turn, from: string, to: string): boolean {
  return mover === 'w' ? to[1]! > from[1]! : to[1]! < from[1]!;
}

/**
 * The feature vector for one candidate, or null when the move cannot be
 * applied to `before` (the re-ranker treats null as "skip this candidate").
 */
export function extractFeatures(
  adapter: GameAdapter,
  before: PositionSnapshot,
  uci: string,
): StyleFeatures | null {
  const from = uci.slice(0, 2);
  const to = uci.slice(2, 4);
  let after: PositionSnapshot;
  try {
    after = adapter.makeMove(before, from, to);
  } catch {
    return null;
  }
  const mover = before.turn;
  const own = mover === 'w' ? 'white' : 'black';
  const foe = mover === 'w' ? 'black' : 'white';

  // --- capture01: from the captured-lists diff (both adapters maintain them,
  // appending what THIS move took) ------------------------------------------
  const takenBefore = before.captured[mover].length;
  const takenAfter = after.captured[mover].length;
  let capture01 = 0;
  let isCapture = false;
  if (takenAfter > takenBefore) {
    isCapture = true;
    const victim = after.captured[mover][takenAfter - 1]!;
    capture01 = adapter.gameId === 'checkers'
      ? victim.toLowerCase() === 'x'
        ? 1
        : 0.33
      : pieceValue(victim) / 9;
  }

  const piece = adapter.pieceAt(before, from);
  const isManLike = piece != null && (piece.char.toLowerCase() === 'p' || piece.char.toLowerCase() === 'm');
  const advance01 = isManLike && advancesTowardPromotion(mover, from, to) ? 1 : 0;

  const afterTable = adapter.attackTable(after);
  const defended = (afterTable[own][squareToIndex(to)] ?? 0) > 0 ? 1 : 0;

  // --- safe ------------------------------------------------------------------
  let safe: number;
  if (adapter.gameId === 'checkers') {
    // A blot: the landing square is attacked by the opponent afterwards.
    safe = (afterTable[foe][squareToIndex(to)] ?? 0) > 0 ? 0 : 1;
  } else {
    // King-safety delta: no MORE opponent attackers over the king's
    // neighbourhood than before (moving the shielding pawn must read as unsafe).
    const kingSq = adapter.kingSquare(after, mover);
    if (kingSq == null) {
      safe = (afterTable[foe][squareToIndex(to)] ?? 0) > 0 ? 0 : 1;
    } else {
      const ring = neighbourhood(squareToIndex(kingSq));
      const beforeTable = adapter.attackTable(before);
      safe = attackerCount(afterTable[foe], ring) <= attackerCount(beforeTable[foe], ring) ? 1 : 0;
    }
  }

  // --- tradeAhead: capture taken while already ahead (white-POV evaluate) ----
  const materialLead = adapter.evaluate(after); // + = white better
  const aheadByAPawn = adapter.gameId === 'checkers'
    ? mover === 'w'
      ? materialLead >= 100
      : materialLead <= -100
    : mover === 'w'
      ? materialLead >= 1
      : materialLead <= -1;
  const tradeAhead = isCapture && aheadByAPawn ? 1 : 0;

  return {
    capture01,
    givesCheck: adapter.isCheck(after) ? 1 : 0,
    advance01,
    defended,
    safe,
    tradeAhead,
  };
}
